/**
 * Variant classification: per-source presentation, severity bucketing, and
 * marker configuration.
 *
 * Pure module — no React, no MUI imports — so it can be reused by tests, list
 * views, the pedigree D3 renderer, and chip rendering in VariantRow.jsx.
 *
 * ## The rule this module follows
 *
 * A variant may carry classifications from several sources: the reporting
 * laboratory, ClinVar, and a clinician who has reviewed it. **We present each
 * source with its own classification and provenance. We do not reconcile them
 * into a single verdict.** Collapsing two disagreeing positions into one
 * displayed value produces a clinical conclusion that existed in neither
 * input — and where the two are a pathogenic ClinVar assertion and a
 * laboratory that declined to call it, that invented conclusion can drive
 * surveillance, cascade testing of relatives, and risk-reducing surgery.
 *
 * Two consequences run through everything below:
 *
 * 1. **A source's own words are displayed verbatim.** Nothing here rewrites
 *    "Pathogenic/Likely pathogenic" or "Conflicting classifications of
 *    pathogenicity" into a tidier tier. Bucketing exists only to decide
 *    whether sources materially disagree and which marker to draw; a string
 *    we cannot bucket is still shown, it simply gets no marker.
 * 2. **Where sources disagree, the marker says so** rather than picking a
 *    winner (`MARKER_KIND.differ`). The detail belongs on the record, where
 *    there is room to attribute it.
 *
 * `leadingClassification` DOES exist, for the cases that need one value: when
 * only one source has an opinion, and for the pedigree analysis engine's
 * notion of "this person carries a pathogenic variant". Its order —
 * clinician → laboratory → ClinVar — is a deliberate rule, not an accident of
 * array order: a clinician's recorded classification is the treating service's
 * own position and leads, while every source stays visible beside it.
 *
 * KEEP THE BUCKETING TABLE IN SYNC with `backend/util/variantClassification.js`.
 * There is no shared package between the two halves of this repo, so the table
 * is duplicated deliberately; both sides pin it with tests over the same input
 * list, so drift fails loudly in both suites.
 */

import { CLINICAL_COLOURS } from '../theme/palette';

export const BUCKET = Object.freeze({
  pathogenic: 'pathogenic',
  uncertain: 'uncertain',
  benign: 'benign'
});

export const MARKER_KIND = Object.freeze({
  tier: 'tier',
  differ: 'differ',
  // A disagreement a clinician has explicitly reviewed and determined.
  // Deliberately its OWN state rather than either of the other two: showing
  // the plain tier would hide that the sources ever disagreed, and continuing
  // to show `differ` would mean a discrepancy could never be resolved except
  // by the laboratory reissuing — a permanent marker no clinician can act on,
  // which is how a genuine safety signal decays into noise.
  reviewed: 'reviewed'
});

// Values that state no classification was made. Distinct from a value we
// cannot interpret: these are absences.
const ABSENT_VALUES = new Set([
  '',
  '-',
  'none',
  'not provided',
  'no classification provided',
  'no assertion provided',
  'no assertion criteria provided',
  'no description found',
  'not specified',
  'unknown'
]);

const normalise = (raw) =>
  raw === null || raw === undefined ? '' : String(raw).trim().toLowerCase();

/** True when the value states that no classification exists. */
export const isAbsentClassification = (raw) => {
  const key = normalise(raw);
  return key === '' || ABSENT_VALUES.has(key);
};

/**
 * Severity bucket for a classification string, tolerant of the compound forms
 * ClinVar publishes.
 * @param {unknown} raw
 * @returns {'pathogenic'|'uncertain'|'benign'|null} null when absent,
 *   unrecognised, or not a pathogenicity statement at all ("drug response",
 *   "risk factor"). Callers must read null as "no opinion", never as benign.
 */
export const classificationBucket = (raw) => {
  const key = normalise(raw);
  if (isAbsentClassification(key)) return null;
  // ClinVar's own conflict verdict is an uncertainty statement, and it
  // contains the substring "pathogenicity" — so it must be tested first.
  if (key.includes('conflicting')) return BUCKET.uncertain;
  if (key.includes('uncertain') || key === 'vus' || key.startsWith('vus '))
    return BUCKET.uncertain;
  if (key.includes('benign')) return BUCKET.benign;
  if (key.includes('pathogenic')) return BUCKET.pathogenic;
  return null;
};

// Compact labels for markers and chips. Falls back to a bucket initial so a
// classification we can bucket but not name still renders something truthful.
const SHORT_LABELS = [
  [/^pathogenic\s*\/\s*likely pathogenic/, 'P/LP'],
  [/^likely pathogenic/, 'LP'],
  [/^pathogenic/, 'P'],
  [/conflicting/, 'CONF'],
  [/^(variant of )?uncertain significance|^vus\b/, 'VUS'],
  [/^benign\s*\/\s*likely benign/, 'B/LB'],
  [/^likely benign/, 'LB'],
  [/^benign/, 'B']
];

const BUCKET_FALLBACK_LABEL = {
  [BUCKET.pathogenic]: 'P',
  [BUCKET.uncertain]: 'VUS',
  [BUCKET.benign]: 'B'
};

/**
 * Short display label for a raw classification string.
 * @returns {string|null} null when there is nothing to label
 */
export const classificationShortLabel = (raw) => {
  const key = normalise(raw);
  if (isAbsentClassification(key)) return null;
  for (const [pattern, label] of SHORT_LABELS) {
    if (pattern.test(key)) return label;
  }
  const bucket = classificationBucket(key);
  return bucket ? BUCKET_FALLBACK_LABEL[bucket] : null;
};

// Exact canonical ACMG tiers, for the few consumers that must distinguish
// Pathogenic from Likely pathogenic precisely — linking a carrier assertion to
// one specific variant, for instance, where the stored enum has separate
// values for the two.
const STRICT_TIERS = {
  pathogenic: 'Pathogenic',
  'likely pathogenic': 'Likely Pathogenic',
  'variant of uncertain significance': 'Variant of Uncertain Significance',
  'uncertain significance': 'Variant of Uncertain Significance',
  vus: 'Variant of Uncertain Significance',
  'likely benign': 'Likely Benign',
  benign: 'Benign'
};

/**
 * Strict tier for a classification string.
 *
 * Deliberately returns null for compound forms ("Pathogenic/Likely
 * pathogenic", "Conflicting classifications of pathogenicity"): a consumer
 * that needs an exact tier must not be handed a guess at which half of a
 * compound classification was meant. Use `classificationBucket` when an
 * approximate severity is enough.
 *
 * @param {unknown} raw
 * @returns {string|null}
 */
export const strictAcmgTier = (raw) => {
  const key = normalise(raw);
  if (isAbsentClassification(key)) return null;
  return STRICT_TIERS[key] || null;
};

// ---------------------------------------------------------------------------
// Per-source view
// ---------------------------------------------------------------------------

/**
 * Every source's position on one variant, in display order, each with its own
 * provenance. Sources with no classification are INCLUDED with
 * `classification: null` — "Laboratory: no classification reported" is
 * clinically meaningful information and must be shown, not omitted.
 *
 * @param {object|null|undefined} variant
 * @returns {Array<{
 *   key: string, label: string, classification: string|null,
 *   bucket: string|null, shortLabel: string|null, provenance: object
 * }>}
 */
export const variantSources = (variant) => {
  if (!variant) return [];

  const entry = (key, label, raw, provenance) => {
    const classification = isAbsentClassification(raw) ? null : String(raw);
    return {
      key,
      label,
      classification,
      bucket: classificationBucket(raw),
      shortLabel: classificationShortLabel(raw),
      provenance
    };
  };

  // A legacy / bare-API shape: a classification with no stated source. Older
  // records and the raw ClinVar API response carry these, so dropping them
  // would silently render a genuinely classified variant as unclassified.
  // Included ONLY when populated — unlike the three named sources, whose
  // absence is itself meaningful and therefore always shown.
  const unattributed = variant.classification ?? variant.description;
  const trailing = isAbsentClassification(unattributed)
    ? []
    : [entry('unattributed', 'Recorded classification', unattributed, {})];

  return [
    entry(
      'clinician',
      'Clinician',
      variant.clinicianInterpretation ?? variant.clinicianClassification,
      {
        by: variant.clinicianInterpretationBy || null,
        at: variant.clinicianInterpretationAt || null
      }
    ),
    entry(
      'lab',
      'Laboratory',
      variant.labInterpretation ?? variant.labClassification,
      {
        testName: variant.sourceTestName || null,
        testDate: variant.sourceTestDate || null
      }
    ),
    entry(
      'clinvar',
      'ClinVar',
      variant.clinvarInterpretation ?? variant.clinvarClassification,
      {
        variationId: variant.clinvarVariationId || null,
        reviewStatus: variant.clinvarReviewStatus || null,
        reviewStars: Number.isFinite(variant.clinvarReviewStars)
          ? variant.clinvarReviewStars
          : null,
        submitterCount: Number.isFinite(variant.clinvarSubmitterCount)
          ? variant.clinvarSubmitterCount
          : null,
        expertPanel: Boolean(variant.clinvarExpertPanel),
        practiceGuideline: Boolean(variant.clinvarPracticeGuideline),
        // ClinVar's OWN conflicting-submissions flag, shown as published.
        internalConflict: Boolean(variant.clinvarInternalConflict),
        lastEvaluated: variant.clinvarLastEvaluated || null,
        retrievedAt: variant.clinvarLastFetchedAt || null,
        datasetVersion: variant.clinvarDatasetVersion || null,
        matchedOn: variant.clinvarMatchedOn || null,
        lookupStatus: variant.clinvarLookupStatus || null,
        researchUseOnly: Boolean(variant.researchUseOnly)
      }
    ),
    ...trailing
  ];
};

/** Sources that actually stated a classification. */
export const classifyingSources = (variant) =>
  variantSources(variant).filter((s) => s.classification !== null);

/**
 * Do two sources materially disagree about this variant?
 *
 * Bucketed comparison: Pathogenic vs Likely pathogenic is concordant, Benign
 * vs Likely benign is concordant, and anything crossing buckets disagrees. A
 * warning that fires on P-vs-LP trains clinicians to ignore the marker.
 *
 * Compares the LABORATORY and ClinVar positions — the discrepancy a clinician
 * needs flagged. A clinician's own classification is their resolution of that
 * discrepancy, so it neither creates nor silences one.
 *
 * @param {object|null|undefined} variant
 * @returns {boolean}
 */
export const hasClassificationConflict = (variant) => {
  if (!variant) return false;
  const lab = classificationBucket(
    variant.labInterpretation ?? variant.labClassification
  );
  const clinvar = classificationBucket(
    variant.clinvarInterpretation ?? variant.clinvarClassification
  );
  if (!lab || !clinvar) return false;
  return lab !== clinvar;
};

/**
 * The state of a clinician's review of this variant's source disagreement.
 *
 * An explicit acknowledgement, never inferred from `clinicianInterpretation`
 * being present: a classification can be recorded without the conflict having
 * been noticed, and treating that as review would silence a warning nobody
 * read.
 *
 * Reviews are APPEND-ONLY: correction happens by appending a new one, never by
 * editing or deleting, so a reclassification by a second clinician reads as
 * exactly that rather than as the first opinion vanishing. `current` is the
 * most recent; `history` is every review, newest first.
 *
 * A review is SCOPED to what was on screen when it was made — both source
 * classifications AND the determination. If any of the three subsequently moves
 * to a different severity bucket, the review no longer covers the current
 * disagreement and goes `stale`, so the marker re-raises rather than a past
 * resolution quietly covering a fresh conflict or appearing to endorse a
 * determination nobody reviewed.
 *
 * @returns {{ reviewed: boolean, stale: boolean,
 *   current: object|null, history: Array<object> }}
 */
export const discrepancyReview = (variant) => {
  const history = Array.isArray(variant?.discrepancyReviews)
    ? [...variant.discrepancyReviews].sort(
        (a, b) => new Date(b?.reviewedAt || 0) - new Date(a?.reviewedAt || 0)
      )
    : [];

  const none = { reviewed: false, stale: false, current: null, history };
  if (history.length === 0) return none;

  const current = history[0];

  // Compare by BUCKET, not by string: the same disagreement rewritten
  // ("Likely benign" → "Benign") is still the disagreement that was reviewed,
  // and re-raising on a wording change would make the resolution useless in
  // practice. A move across buckets is a different disagreement.
  const sameBucket = (then, now) =>
    classificationBucket(then) === classificationBucket(now);

  // All three must still hold. The determination is included because a review
  // endorses a SPECIFIC clinical position: if someone later changes the
  // classification without re-reviewing, the tick would otherwise imply the new
  // value had been reviewed when it had not.
  const stillApplies =
    sameBucket(current.labAtReview, variant?.labInterpretation) &&
    sameBucket(current.clinvarAtReview, variant?.clinvarInterpretation) &&
    sameBucket(current.determination, variant?.clinicianInterpretation);

  return stillApplies
    ? { reviewed: true, stale: false, current, history }
    : { reviewed: false, stale: true, current, history };
};

/** True when ANY variant in the list has a lab/ClinVar disagreement. */
export const geneHasClassificationConflict = (variants) => {
  if (!Array.isArray(variants)) return false;
  return variants.some(hasClassificationConflict);
};

/**
 * The single value to use where only one is possible — see the module header
 * for why this is deliberately narrow. Order: clinician → laboratory →
 * ClinVar. Returns the source's RAW text plus its bucket, never a rewritten
 * tier.
 *
 * @returns {{ sourceKey: string, sourceLabel: string,
 *   classification: string, bucket: string|null } | null}
 */
export const leadingClassification = (variant) => {
  const stated = classifyingSources(variant);
  if (stated.length === 0) return null;
  const s = stated[0];
  return {
    sourceKey: s.key,
    sourceLabel: s.label,
    classification: s.classification,
    bucket: s.bucket
  };
};

// ---------------------------------------------------------------------------
// Markers
// ---------------------------------------------------------------------------

// Marker colours come from the clinical register (src/theme/palette.js).
const BUCKET_MARKER = {
  [BUCKET.pathogenic]: {
    color: CLINICAL_COLOURS.pathogenic,
    label: 'Pathogenic'
  },
  [BUCKET.uncertain]: {
    color: CLINICAL_COLOURS.uncertainSignificance,
    label: 'Uncertain significance'
  }
  // Benign is deliberately unmarked: a pedigree marker exists to draw
  // attention, and a benign call needs none. It remains fully visible on the
  // record, and a benign-vs-pathogenic disagreement still raises the `differ`
  // marker below.
};

// Amber, distinct from both the pathogenic red and the uncertain grey: this
// marker means "look at the record", not "this is severe".
const DIFFER_MARKER = Object.freeze({
  kind: MARKER_KIND.differ,
  color: CLINICAL_COLOURS.classificationsDiffer,
  label: 'Classifications differ — see record',
  shortLabel: '≠',
  bucket: null
});

// A reviewed disagreement shows the clinician's OWN determination, because a
// human has now made that call — which is categorically different from the
// software inventing one, and is the most defensible thing this product can
// display. The check mark records that the sources disagreed and were
// adjudicated, so nothing is concealed.
const reviewedMarker = (leading, review) => {
  const config = leading?.bucket ? BUCKET_MARKER[leading.bucket] : null;
  return {
    kind: MARKER_KIND.reviewed,
    // Falls back to the reviewed-benign slate (#5B7A8C, not the differ amber)
    // when the clinician's determination is benign, which draws no tier of
    // its own — the row must still be findable, and "reviewed" is the
    // information that matters here.
    color: config ? config.color : CLINICAL_COLOURS.reviewedDiscrepancy,
    label: leading
      ? `${leading.classification} — clinician-reviewed discrepancy`
      : 'Clinician-reviewed discrepancy',
    reviewedBy: review?.current?.reviewedBy || null,
    reviewedAt: review?.current?.reviewedAt || null,
    shortLabel: leading
      ? `${classificationShortLabel(leading.classification) || '?'}✓`
      : '✓',
    bucket: leading?.bucket || null,
    review
  };
};

/**
 * Marker for ONE variant.
 *
 * Where the laboratory and ClinVar disagree the marker is neutral — it
 * reports that classifications exist and differ, and does not choose one.
 * Otherwise it reflects the leading source's own classification.
 *
 * @returns {{ kind: string, color: string, label: string,
 *   shortLabel: string, bucket: string|null } | null} null when nothing
 *   should be drawn
 */
export const variantMarker = (variant) => {
  if (!variant) return null;

  if (hasClassificationConflict(variant)) {
    const review = discrepancyReview(variant);
    if (review.reviewed) {
      return reviewedMarker(leadingClassification(variant), review);
    }
    // Unreviewed, or reviewed against classifications that have since moved.
    // A stale review re-raises: the resolution cannot outlive the disagreement
    // it resolved.
    return review.stale
      ? {
          ...DIFFER_MARKER,
          label:
            'Classifications differ — a previous review no longer applies, see record',
          review
        }
      : DIFFER_MARKER;
  }

  const leading = leadingClassification(variant);
  if (!leading || !leading.bucket) return null;

  const config = BUCKET_MARKER[leading.bucket];
  if (!config) return null;

  return {
    kind: MARKER_KIND.tier,
    color: config.color,
    // The source's own words, so the tooltip never paraphrases a
    // classification.
    label: leading.classification,
    shortLabel:
      classificationShortLabel(leading.classification) || config.label,
    bucket: leading.bucket
  };
};

/**
 * Marker for a GENE label row, which may cover several variants.
 *
 * Aggregation is unavoidable here — one row, one marker — so it is made
 * explicit rather than silent: `variantCount` and `markedCount` let the caller
 * say "most significant of N variants" instead of implying the gene has a
 * single classification. Any disagreement anywhere, or disagreement between
 * two variants' own buckets, yields the neutral differ marker.
 *
 * @param {Array<object>|null|undefined} variants
 * @returns {object|null}
 */
export const geneMarker = (variants) => {
  if (!Array.isArray(variants) || variants.length === 0) return null;

  // Evaluate every CLASSIFIED variant, not just the ones that would draw a
  // marker on their own. A benign variant draws nothing by itself, but a
  // benign call sitting beside a pathogenic one on the same gene is precisely
  // the disagreement a single worst-wins marker used to hide.
  const classified = variants
    .map((variant) => ({
      variant,
      conflict: hasClassificationConflict(variant),
      leading: leadingClassification(variant)
    }))
    .filter((c) => c.conflict || c.leading?.bucket);

  if (classified.length === 0) return null;

  // Source disagreement and its review are INDEPENDENT of whatever the row's
  // primary signal turns out to be. A gene can carry variants in different
  // severity buckets (its own reason to show the differ glyph) while every
  // source disagreement on it has been reviewed — and a reader needs both
  // facts. Previously the review was only visible when it happened to be the
  // primary signal, so a reviewed discrepancy on a gene whose variants also
  // differ showed no tick at all.
  const disagreeing = classified.filter((c) => c.conflict);
  const reviewedDisagreements = disagreeing.filter(
    (c) => discrepancyReview(c.variant).reviewed
  );

  const counts = {
    variantCount: variants.length,
    markedCount: classified.length,
    sourceDisagreements: disagreeing.length,
    reviewedDisagreements: reviewedDisagreements.length,
    // True only when there WAS something to review and all of it has been.
    allDisagreementsReviewed:
      disagreeing.length > 0 &&
      reviewedDisagreements.length === disagreeing.length
  };

  // An UNREVIEWED (or stale-reviewed) disagreement anywhere still demands
  // attention and dominates the row. A reviewed one does not: it has been
  // adjudicated, so it must not keep the whole gene flagged forever — that is
  // the permanence this state exists to remove.
  const unresolved = disagreeing.filter(
    (c) => !discrepancyReview(c.variant).reviewed
  );
  if (unresolved.length) {
    const anyStale = unresolved.some((c) => discrepancyReview(c.variant).stale);
    return {
      ...DIFFER_MARKER,
      ...(anyStale
        ? {
            label:
              'Classifications differ — a previous review no longer applies, see record'
          }
        : {}),
      ...counts
    };
  }

  const buckets = new Set(
    classified.map((c) => c.leading?.bucket).filter(Boolean)
  );

  // Two variants on the same gene classified into different buckets is not a
  // source disagreement, but it is equally misrepresented by showing only the
  // worst one — the second classification simply vanishes.
  if (buckets.size > 1) {
    return {
      ...DIFFER_MARKER,
      label: 'Variants classified differently — see record',
      ...counts
    };
  }

  const marker = variantMarker(classified[0].variant);
  // Single shared bucket with nothing to draw (all benign).
  if (!marker) return null;

  return {
    ...marker,
    ...counts,
    // True when this marker speaks for more than one classified variant, so
    // the caller can label the aggregation instead of hiding it.
    aggregated: classified.length > 1
  };
};
